<template>
  <div class="px-4 py-6 md:px-8 md:py-12 select-none text-left min-h-screen transition-colors duration-300 font-sans"
    :class="isDark ? 'bg-black text-zinc-300' : 'bg-[#FDFCF8] text-[#7A7367]'">
    <main class="max-w-4xl mx-auto space-y-6">
      
      <div class="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b pb-6" 
        :class="isDark ? 'border-zinc-900' : 'border-[#E5E0D8]'">
        <div class="space-y-1.5">
          <div class="text-[10px] font-bold uppercase tracking-[0.2em]" :class="isDark ? 'text-zinc-500' : 'text-[#A69B88]'">
            Espace Membre Élite
          </div>
          <h1 class="text-2xl font-semibold tracking-tight" :class="isDark ? 'text-white' : 'text-[#2D2924]'">
            Vos Réservations
          </h1>
          <p class="text-xs font-light max-w-xl leading-relaxed" :class="isDark ? 'text-zinc-500' : 'text-[#8C8579]'">
            Suivez, recherchez et fragmentez vos sessions programmées ou passées en toute simplicité.
          </p>
        </div>
      </div>
  
      <nav id="filterTabs" class="flex p-1 rounded-xl transition-all border" 
        :class="isDark ? 'bg-zinc-900/40 border-zinc-900' : 'bg-[#F7F5F0] border-[#E5E0D8]'" role="tablist">
        <button
          v-for="(tab, index) in tabs"
          :key="index"
          role="tab"
          :aria-selected="activeTab === tab.value"
          @click="activeTab = tab.value; currentPage = 1;"
          class="flex-1 text-center py-2.5 rounded-lg text-xs font-semibold tracking-wider uppercase transition-all duration-200 whitespace-nowrap focus:outline-none"
          :class="[
            activeTab === tab.value
              ? isDark ? 'bg-zinc-800 text-white shadow-md' : 'bg-white text-[#2D2924] shadow-sm font-bold'
              : isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-[#8C8579] hover:text-[#2D2924]'
          ]"
        >
          {{ tab.label }}
        </button>
      </nav>

      <div class="relative w-full rounded-xl border transition-all"
        :class="isDark ? 'bg-zinc-950/40 border-zinc-900 focus-within:border-zinc-700' : 'bg-[#F7F5F0]/50 border-[#E5E0D8] focus-within:border-[#A69B88]'">
        <span class="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
          <i class="fas fa-search text-xs opacity-50"></i>
        </span>
        <input 
          v-model="searchQuery"
          type="text"
          placeholder="Rechercher un salon, une prestation (ex: Coupe)..."
          class="w-full pl-10 pr-4 py-3 bg-transparent text-xs font-light focus:outline-none rounded-xl"
          :class="isDark ? 'text-white placeholder-zinc-600' : 'text-[#2D2924] placeholder-[#D6D0C5]'"
          @input="currentPage = 1"
        />
        <span v-if="searchQuery" @click="searchQuery = ''" class="absolute inset-y-0 right-0 flex items-center pr-4 cursor-pointer hover:text-rose-500 transition-colors">
          <i class="fas fa-times text-xs"></i>
        </span>
      </div>
  
      <section class="space-y-4" id="bookingsList">
        <div v-if="paginatedBookings.length === 0" class="text-center py-12 border border-dashed rounded-2xl"
          :class="isDark ? 'border-zinc-900 text-zinc-600' : 'border-[#E5E0D8] text-[#8C8579]'">
          <i class="far fa-calendar-times text-2xl mb-2 block opacity-40"></i>
          <p class="text-xs font-light">Aucun rendez-vous ne correspond à vos critères.</p>
        </div>

        <article
          v-for="booking in paginatedBookings"
          :key="booking._id"
          class="group rounded-2xl border p-5 md:p-6 transition-all duration-300 grid grid-cols-1 md:grid-cols-12 gap-5 items-center relative"
          :class="[
            booking.active 
              ? isDark ? 'bg-zinc-900/20 border-zinc-800 shadow-xl' : 'bg-white border-[#A69B88] shadow-md shadow-zinc-900/5'
              : isDark ? 'bg-zinc-950/40 border-zinc-900/60' : 'bg-[#F7F5F0]/30 border-[#E5E0D8]'
          ]"
        >
          <div class="flex md:flex-col items-center md:items-start justify-between md:justify-center md:border-r pb-4 md:pb-0 md:pr-4 md:col-span-3 gap-3"
            :class="isDark ? 'border-zinc-900' : 'border-[#E5E0D8]'">
            <div class="flex items-center md:flex-col items-baseline md:items-start gap-2 md:gap-0">
              <span class="text-3xl font-bold tracking-tighter" :class="isDark ? 'text-white' : 'text-[#2D2924]'">
                {{ booking.day }}
              </span>
              <span class="text-[10px] font-bold uppercase tracking-widest" :class="isDark ? 'text-zinc-500' : 'text-[#A69B88]'">
                {{ booking.monthShort }}
              </span>
            </div>
            
            <div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium font-mono tracking-tight"
              :class="isDark ? 'bg-zinc-900 text-zinc-300' : 'bg-[#F7F5F0] text-[#2D2924]'">
              <i class="far fa-clock text-[10px]" :class="isDark ? 'text-zinc-500' : 'text-[#A69B88]'"></i>
              {{ booking.timeRange }}
            </div>
          </div>
  
          <div class="space-y-3 md:col-span-5 text-left">
            <div class="space-y-1">
              <div v-if="booking.statut === 'annulé'" class="inline-block text-[10px] font-bold uppercase tracking-wider text-rose-500">
                Session Révoquée
              </div>
              <h3 class="text-base font-semibold tracking-tight leading-snug" :class="isDark ? 'text-white' : 'text-[#2D2924]'">
                {{ booking.services[0].nom }}
              </h3>
              <div class="flex items-center gap-1.5 text-xs" :class="isDark ? 'text-zinc-400' : 'text-[#8C8579]'">
                <i class="fas fa-map-marker-alt text-[10px]" :class="isDark ? 'text-zinc-700' : 'text-[#D6D0C5]'"></i>
                <span class="font-medium truncate">{{ booking.firm.nom_societe }}</span>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-2 text-[11px]">
              <span v-if="booking.employe" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs"
                :class="isDark ? 'bg-zinc-900/50 border-zinc-800 text-zinc-300' : 'bg-white border-[#E5E0D8] text-[#7A7367]'">
                <i class="far fa-user text-[9px] text-[#A69B88]"></i>
                {{ booking.employe.prenom }}
              </span>

              <span class="font-semibold px-2 py-0.5 rounded-full"
                :class="isDark ? 'bg-zinc-900 text-zinc-100' : 'bg-[#2D2924] text-white'">
                {{ booking.services[0].prix }} €
              </span>

              <span v-if="booking.isPaid || booking.isPaidCompletely" class="inline-flex items-center gap-1 text-emerald-600 font-medium">
                <i class="fas fa-check-circle text-[10px]"></i> Payé
              </span>
              <span v-else class="text-[10px] uppercase font-bold tracking-wider opacity-60">
                Sur place
              </span>
            </div>

            <div v-if="booking.note" class="text-[11px] px-2.5 py-1.5 rounded-lg border italic font-light max-w-full truncate"
              :class="isDark ? 'bg-zinc-900/20 border-zinc-800/60 text-zinc-400' : 'bg-amber-50/40 border-amber-200/50 text-[#8C8579]'">
              <span class="font-medium not-italic">Note :</span> « {{ booking.note }} »
            </div>
          </div>
  
          <div class="flex flex-wrap items-center justify-end gap-2 pt-4 md:pt-0 border-t md:border-t-0 md:col-span-4 w-full"
            :class="isDark ? 'border-zinc-900' : 'border-[#E5E0D8]'">
            
            <button
              v-if="booking.statut !== 'annulé'"
              type="button"
              @click="openReplanifyModal(booking)"
              class="flex-1 sm:flex-initial px-4 py-2.5 rounded-xl text-[11px] font-semibold uppercase tracking-wider transition-all duration-200 border text-center"
              :class="isDark 
                ? 'bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800' 
                : 'bg-white border-[#E5E0D8] text-[#7A7367] hover:border-[#A69B88] hover:text-[#2D2924] shadow-sm'"
            >
              Replanifier
            </button>

            <button
              v-if="booking.statut === 'terminer' && !booking.avisDejaLaisse"
              type="button"
              @click="openReviewModal(booking)"
              class="flex-1 sm:flex-initial px-4 py-2.5 rounded-xl text-[11px] font-semibold uppercase tracking-wider transition-all duration-200 border text-center"
              :class="isDark 
                ? 'bg-white text-black border-white hover:bg-[#D5CCBC]' 
                : 'bg-[#2D2924] text-white border-[#2D2924] hover:bg-[#A69B88] shadow-md shadow-zinc-900/10'"
            >
              Laisser un avis
            </button>

            <button
              v-if="booking.statut === 'confirmé'"
              type="button"
              title="Annuler le rendez-vous"
              @click="openCancelDialog(booking._id)"
              class="p-2.5 rounded-xl border transition-all text-rose-500 hover:bg-rose-500/10"
              :class="isDark ? 'border-zinc-900 bg-zinc-950' : 'border-[#E5E0D8] bg-[#FDFCF8] shadow-sm'"
            >
              <i class="fas fa-trash-alt text-xs"></i>
            </button>
          </div>
        </article>
      </section>

      <div v-if="totalPages > 1" class="flex items-center justify-between pt-4 border-t text-[11px] font-semibold uppercase tracking-wider"
        :class="isDark ? 'border-zinc-900' : 'border-[#E5E0D8]'">
        <button 
          :disabled="currentPage === 1"
          @click="currentPage--"
          class="px-3 py-2 rounded-lg border transition-all disabled:opacity-30"
          :class="isDark ? 'bg-zinc-950 border-zinc-900 hover:bg-zinc-900' : 'bg-white border-[#E5E0D8] hover:bg-[#F7F5F0] text-[#7A7367]'"
        >
          <i class="fas fa-chevron-left mr-1"></i> Précédent
        </button>
        
        <span :class="isDark ? 'text-zinc-500' : 'text-[#8C8579]'">
          Page {{ currentPage }} sur {{ totalPages }}
        </span>

        <button 
          :disabled="currentPage === totalPages"
          @click="currentPage++"
          class="px-3 py-2 rounded-lg border transition-all disabled:opacity-30"
          :class="isDark ? 'bg-zinc-950 border-zinc-900 hover:bg-zinc-900' : 'bg-white border-[#E5E0D8] hover:bg-[#F7F5F0] text-[#7A7367]'"
        >
          Suivant <i class="fas fa-chevron-right ml-1"></i>
        </button>
      </div>

      <div v-if="isCancelDialogOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
  <div class="bg-white p-6 rounded-2xl shadow-xl w-full max-w-sm mx-4" :class="isDark ? 'bg-zinc-900 border border-zinc-800' : 'bg-white'">
    <h2 class="text-lg font-bold mb-4" :class="isDark ? 'text-white' : 'text-gray-900'">Confirmer l'annulation</h2>
    <p class="text-sm mb-4" :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Veuillez indiquer le motif de l'annulation :</p>
    
    <textarea 
      v-model="cancelReason" 
      class="w-full p-3 rounded-lg border mb-4 text-sm focus:outline-none"
      :class="isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'border-gray-300'"
      placeholder="Ex: Emploi du temps modifié..."
    ></textarea>

    <div class="flex gap-3">
      <button @click="isCancelDialogOpen = false" class="flex-1 py-2 rounded-lg text-sm font-semibold border" :class="isDark ? 'border-zinc-700 text-zinc-400' : 'border-gray-300 text-gray-600'">
        Retour
      </button>
      <button @click="submitCancellation" class="flex-1 py-2 rounded-lg text-sm font-semibold bg-rose-500 text-white">
        Confirmer
      </button>
    </div>
  </div>
</div>

<!-- Modale Avis -->
<div v-if="showReviewModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
  <div class="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md" :class="isDark ? 'bg-zinc-900 border border-zinc-800' : 'bg-white'">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-bold" :class="isDark ? 'text-white' : 'text-gray-900'">Laisser un avis</h2>
      <button @click="showReviewModal = false" class="text-gray-400 hover:text-gray-600">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Note Établissement -->
    <div class="mb-4">
      <label class="text-xs font-semibold block mb-2" :class="isDark ? 'text-zinc-300' : 'text-gray-700'">Note de l'établissement</label>
      <RatingInput v-model="reviewForm.note_etablissement" />
    </div>

    <!-- Note Prestations -->
    <div class="mb-4">
      <label class="text-xs font-semibold block mb-2" :class="isDark ? 'text-zinc-300' : 'text-gray-700'">Note des prestations</label>
      <RatingInput v-model="reviewForm.note_prestations" />
    </div>

    <!-- Note Employé -->
    <div class="mb-4">
      <label class="text-xs font-semibold block mb-2" :class="isDark ? 'text-zinc-300' : 'text-gray-700'">Note de l'employé</label>
      <RatingInput v-model="reviewForm.note_employe" />
    </div>

    <!-- Avis -->
    <div class="mb-4">
      <label class="text-xs font-semibold block mb-2" :class="isDark ? 'text-zinc-300' : 'text-gray-700'">Votre avis (minimum 10 caractères)</label>
      <textarea
        v-model="reviewForm.avis"
        rows="4"
        class="w-full p-3 rounded-lg border text-sm focus:outline-none focus:ring-2"
        :class="isDark ? 'bg-zinc-800 border-zinc-700 text-white focus:ring-zinc-600' : 'border-gray-300 focus:ring-blue-200'"
        placeholder="Partagez votre expérience..."
      ></textarea>
      <p class="text-xs text-gray-400 mt-1">{{ reviewForm.avis.length }} caractères</p>
    </div>

    <div class="flex gap-3">
      <button
        @click="showReviewModal = false"
        class="flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-colors"
        :class="isDark ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'border-gray-300 hover:bg-gray-50'"
      >
        Annuler
      </button>
      <button
        @click="submitReview"
        class="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
      >
        Envoyer l'avis
      </button>
    </div>
  </div>
</div>
    </main>
  </div>
</template>
<style scoped>
/* Technical system view resets for carousels scrollbars */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
  
<script>
import axios from 'axios'
import { useTheme } from '@/composables/useTheme';
import RatingInput from '../components/RatingInput.vue';

export default {
  name: 'BookingTabs',
  components: {
    RatingInput
  },
  setup() {
    const { isDark } = useTheme();
    return { isDark };
  },
  data() {
    return {
      showReviewModal: false,
      searchQuery: '',     // Gère le terme tapé dans la barre de recherche
    currentPage: 1,     // Page active globale
    itemsPerPage: 4,
       reviewForm: {
  avis: '',
  note_etablissement: 0,
  note_prestations: 0,
  note_employe: 0,
},

      tabs: [
        { label: 'À venir', value: 'confirmé' },
        { label: 'Passées', value: 'terminer' },
        { label: 'Annulées', value: 'annulé' },
      ],
      activeTab: 'confirmé',
          motifAnnulation: '',
      bookings: [],
      showCancelModal: false,
    isCancelDialogOpen: false,
      showAnnulModal : false,
      showModifyModal: false,
      selectedServices : [],
       selectedBookingIndex: null,
      selectedBooking: null,
      showConfirmDeleteService: false,
      indexServiceToDelete: null,
      showReplanifyModal: false,
      cancelReason: '',
      serviceToDeleteId: null,
       activeMenu: null,
       selectedAppointmentId: null,
    }
  },

computed: {
  // Filtre par Onglet + Recherche textuelle + TRI CHRONOLOGIQUE CROISSANT
  filteredBookings() {
    // 1. On commence par filtrer les données comme avant
    const result = this.bookings.filter(b => {
      // Sécurité selon les différents états de votre API (ex: "confirmé", "annulé")
      // Si activeTab est lié à b.statut
      const matchTab = b.statut === this.activeTab;
      
      const salonNom = b.firm?.nom_societe?.toLowerCase() || '';
      const serviceNom = b.services?.[0]?.nom?.toLowerCase() || '';
      const query = this.searchQuery.toLowerCase();

      const matchSearch = salonNom.includes(query) || serviceNom.includes(query);
      
      return matchTab && matchSearch;
    });

    // 2. ON TRIE : Du plus proche (bientôt) au plus lointain dans le temps
    return result.sort((a, b) => {
      return new Date(a.date_rdv) - new Date(b.date_rdv);
    });
  },

  // (Conservez les autres computed comme totalPages et paginatedBookings inchangés)
  totalPages() {
    return Math.ceil(this.filteredBookings.length / this.itemsPerPage) || 1;
  },
  paginatedBookings() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredBookings.slice(start, end);
  }
},

  methods: {
async submitReview() {
  try {
    const token = localStorage.getItem('clientToken');

    // Validation des champs
    if (!this.reviewForm.avis || this.reviewForm.avis.length < 10) {
      alert('Votre avis doit contenir au moins 10 caractères.');
      return;
    }

    if (!this.reviewForm.note_etablissement || !this.reviewForm.note_prestations || !this.reviewForm.note_employe) {
      alert('Veuillez donner une note pour tous les critères (1 à 5 étoiles).');
      return;
    }

    const response = await fetch('https://bookmysalon-967a856b16b6.herokuapp.com/api/reviews', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        avis: this.reviewForm.avis,
        note_etablissement: this.reviewForm.note_etablissement,
        note_prestations: this.reviewForm.note_prestations,
        note_employe: this.reviewForm.note_employe,
        employeId: this.selectedBooking.employe?._id || null,
        firmId: this.selectedBooking.firm._id,
        appointmentId: this.selectedBooking._id
      })
    });

    const data = await response.json();

    if (data.success) {
      this.showReviewModal = false;
      this.selectedBooking.avisDejaLaisse = true;
      alert('Merci pour votre avis ! Il sera visible après validation par le salon.');
    } else {
      if (response.status === 400 && data.message.includes('déjà laissé')) {
        this.showReviewModal = false;
        this.selectedBooking.avisDejaLaisse = true;
        alert('Vous avez déjà laissé un avis pour ce rendez-vous. Merci !');
      } else {
        alert(`Erreur : ${data.message || 'Impossible d\'envoyer votre avis.'}`);
      }
    }
  } catch (error) {
    console.error('Erreur lors de l\'envoi de l\'avis :', error);
    alert('Erreur lors de l\'envoi de l\'avis.');
  }
},


    openReviewModal(booking) {
    this.selectedBooking = booking;
    this.showReviewModal = true;
    this.reviewForm = {
      avis: '',
      note_etablissement: null,
      note_prestations: null,
      note_employe: null,
    };
  },
    openCancelDialog(id) {
    this.selectedAppointmentId = id;
    this.cancelReason = '';
    this.isCancelDialogOpen = true;
  },
  async submitCancellation() {
    if (!this.cancelReason || !this.selectedAppointmentId) return;

    try {
      const token = localStorage.getItem('clientToken');

      const response = await fetch(`https://bookmysalon-967a856b16b6.herokuapp.com/api/appointment/annuler/${this.selectedAppointmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ motif: this.cancelReason })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erreur inconnue');
      }

      alert('✅ Rendez-vous annulé avec succès');
      this.isCancelDialogOpen = false;
      this.fetchBookings()

    } catch (error) {
      console.error('Erreur d\'annulation :', error);
      alert('❌ Échec de l\'annulation : ' + error.message);
    }
  },
  toggleMenu(bookingId) {
    this.activeMenu = this.activeMenu === bookingId ? null : bookingId;
  },

openCancelModal(bookingId) {
    const booking = this.filteredBookings.find(b => b._id === bookingId);
    if (booking) {
      this.selectedBooking = booking;
      this.showCancelModal = true;
    }
  },

    openModifyModal(bookingId) {
    const booking = this.filteredBookings.find(b => b._id === bookingId);
    if (booking) {
      this.selectedBooking = booking;
      this.showModifyModal = true;
    }
  },
    closeModifyModal() {
      this.showModifyModal = false
      this.selectedBooking = null
      this.selectedBookingIndex = null
    },

confirmDeleteService(serviceId) {
  this.serviceToDeleteId = serviceId;
  this.showConfirmDeleteService = true;
},

    cancelDeleteService() {
      this.showConfirmDeleteService = false
      this.indexServiceToDelete = null
    },

async deleteService() {
  try {
    const services = this.selectedBooking.services;

    const serviceToDelete = services.find(s => s._id === this.serviceToDeleteId);
    if (!serviceToDelete) {
      console.warn("Service à supprimer introuvable avec l'ID :", this.serviceToDeleteId);
      return;
    }

    const payload = {
      servicesASupprimer: [this.serviceToDeleteId],
    };

    if (services.length === 1) {
      if (!this.motifAnnulation || this.motifAnnulation.trim() === "") {
        alert("Veuillez sélectionner un motif d'annulation.");
        return;
      }
      payload.motif = this.motifAnnulation;
    }

    const response = await axios.put(
      `https://bookmysalon-967a856b16b6.herokuapp.com/api/appointment/supprimer-prestations/${this.selectedBooking._id}`,
      payload,
      { headers: { Authorization: `Bearer ${localStorage.getItem("clientToken")}` } }
    );

    this.selectedBooking = response.data.rendezVous;
    this.showConfirmDeleteService = false;
    this.motifAnnulation = '';
    this.serviceToDeleteId = null;
  } catch (error) {
    console.error("Erreur suppression service :", error);
  }
}
,


    async saveModifications() {
      try {
        // Ici tu envoies les modifications (services modifiés) au backend
        const token = localStorage.getItem('clientToken')

        const updatedServices = this.selectedBooking.services.map(s => s._id)

        await axios.put(`https://bookmysalon-967a856b16b6.herokuapp.com/api/appointment/${this.selectedBooking._id}`, {
          services: updatedServices,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })

        // Mets à jour la liste locale
        this.bookings[this.selectedBookingIndex].services = [...this.selectedBooking.services]

        this.closeModifyModal()
        alert('Modifications sauvegardées')
      } catch (err) {
        console.error('Erreur lors de la sauvegarde :', err)
        alert('Erreur lors de la sauvegarde')
      }
    },

    async cancelAppointment(appointmentId) {
      try {
        const token = localStorage.getItem('clientToken')
        await axios.delete(`https://bookmysalon-967a856b16b6.herokuapp.com/api/appointment/${appointmentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        alert('Rendez-vous annulé.')
        this.fetchBookings()
      } catch (err) {
        console.error('Erreur annulation rendez-vous :', err)
        alert('Erreur lors de l\'annulation')
      }
    },

openReplanifyModal(booking) {
  const serviceIds = booking.services.map(service => service._id);

  const firmId = booking.firm._id;

  this.$router.push({
    path: `/rendez-vous/${firmId}`,
    query: {
      replanifier: true,
      bookingId: booking._id,
      service: serviceIds.join(',') // Note: c’est "service" ici, pas "replanifier"
    }
  });
},


    confirmCancellation() {
      alert(`Rendez-vous annulé pour la raison : ${this.cancelReason}`)
      this.showCancelModal = false
      this.cancelReason = ''
    },

   async fetchBookings() {
  try {
    const token = localStorage.getItem('clientToken')
    if (!token) throw new Error('Token client manquant')

    const res = await axios.get('https://bookmysalon-967a856b16b6.herokuapp.com/api/appointment/mes-rendez-vous', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

          console.log(res.data)


    this.bookings = res.data.rendezVous.map(rdv => {
      const dateObj = new Date(rdv.date_rdv)
      const startHour = dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      const endDateObj = new Date(dateObj.getTime() + rdv.duree_minutes * 60000)
      const endHour = endDateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

      // Format de l'heure complète : 07h00 - 09h00
      const timeRange = `${startHour} - ${endHour}`

      // Format du jour (ex: 30)
      const day = dateObj.getDate()

      // Mois abrégé en majuscule (ex: JUI pour juin)
      const monthShort = dateObj.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()

      return {
        ...rdv,
        active: false,
        timeRange,
        day,
        monthShort,
      }
    })
  } catch (err) {
    console.error('Erreur récupération rendez-vous client :', err)
  }
},

    formatTimeRange(startDate, durationMinutes) {
      const start = new Date(startDate)
      const end = new Date(start.getTime() + durationMinutes * 60000)

      const format = (date) =>
        date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
        })

      return `${format(start)} - ${format(end)}`
    },
  },

  mounted() {
    this.fetchBookings()
  }
}
</script>

  
  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
  
  .font-inter {
    font-family: 'Inter', sans-serif;
  }
  
  .scrollbar-thin::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 3px;
  }
  </style>
  